import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { styled } from '@mui/material'

interface Props {
  /** Ref to the horizontally scrollable element this proxy mirrors. */
  targetRef: React.RefObject<HTMLElement>
}

const BAR_HEIGHT = 24
// Anel transparente de cada lado do polegar. Define a espessura visível:
// BAR_HEIGHT - 2 * INSET. Mantém a pilula centrada na trilha.
const INSET = 5

const Bar = styled('div')(({ theme }) => {
  const palette = theme.palette as any
  // Um passo mais escuro que o cinza de borda: a barra flutua sobre a tabela e
  // precisa se ler como controle, não como divisória.
  const thumb = palette.neutral?.[450] ?? '#828282'
  const thumbHover = palette.neutral?.[300] ?? '#646664'
  const track = palette.neutral?.[700] ?? '#E3E6E3'
  return {
    // Fixed to the viewport (and portalled to <body>) so no ancestor's overflow
    // or transform can break it the way `position: sticky` would.
    position: 'fixed',
    bottom: 0,
    height: BAR_HEIGHT,
    overflowX: 'auto',
    overflowY: 'hidden',
    zIndex: 1200,
    // Sem painel: só a trilha e o polegar flutuam sobre o conteúdo.
    background: 'transparent',
    pointerEvents: 'auto',
    // `auto` (e não `thin`): no Firefox o thin fica fino demais pra ser achado.
    scrollbarWidth: 'auto',
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar': {
      height: BAR_HEIGHT,
      backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-track': {
      // A trilha visível é o que denuncia que dá pra arrastar antes mesmo de o
      // usuário achar o polegar.
      backgroundColor: track,
      borderRadius: 999,
      border: `${INSET}px solid transparent`,
      backgroundClip: 'content-box'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: 999,
      border: `${INSET}px solid transparent`,
      backgroundClip: 'content-box',
      minWidth: 48
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: thumbHover
    }
  }
})

interface Metrics {
  visible: boolean
  left: number
  width: number
  scrollWidth: number
}

const HIDDEN: Metrics = { visible: false, left: 0, width: 0, scrollWidth: 0 }

/**
 * A horizontal scrollbar pinned to the bottom of the viewport while a wide
 * table is in view, so the user doesn't have to scroll the page down to reach
 * the native one. Mirrors `scrollLeft` both ways with the target element and
 * hides itself when there's no overflow or the native scrollbar is on screen.
 */
const StickyHorizontalScrollbar: React.FC<Props> = ({ targetRef }) => {
  const proxyRef = useRef<HTMLDivElement>(null)
  // Guards against the scroll-sync feedback loop between the two elements.
  const syncing = useRef(false)
  const rafId = useRef<number | null>(null)

  const [metrics, setMetrics] = useState<Metrics>(HIDDEN)

  const measure = useCallback(() => {
    const target = targetRef.current
    if (!target) {
      setMetrics(HIDDEN)
      return
    }
    const overflows = target.scrollWidth - target.clientWidth > 1
    const rect = target.getBoundingClientRect()
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight
    // Show only when the table overflows horizontally, is at least partially in
    // view, and its bottom edge (where the native scrollbar lives) is below the
    // fold — otherwise the native scrollbar is already reachable.
    const visible = overflows && rect.top < viewportH && rect.bottom > viewportH
    setMetrics({
      visible,
      left: rect.left,
      width: target.clientWidth,
      scrollWidth: target.scrollWidth
    })
  }, [targetRef])

  const scheduleMeasure = useCallback(() => {
    if (rafId.current != null) return
    rafId.current = window.requestAnimationFrame(() => {
      rafId.current = null
      measure()
    })
  }, [measure])

  useEffect(() => {
    const target = targetRef.current
    if (!target) return

    measure()

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleMeasure)
        : null
    ro?.observe(target)

    window.addEventListener('resize', scheduleMeasure)
    // Capture phase: page/inner scrolls don't bubble, so we listen globally.
    window.addEventListener('scroll', scheduleMeasure, true)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
      window.removeEventListener('scroll', scheduleMeasure, true)
      if (rafId.current != null) window.cancelAnimationFrame(rafId.current)
    }
  }, [targetRef, measure, scheduleMeasure])

  // Two-way scrollLeft sync (re-bound whenever the bar (re)mounts).
  useEffect(() => {
    const target = targetRef.current
    const proxy = proxyRef.current
    if (!target || !proxy) return

    // Mirror one element's scrollLeft onto the other. We can't rely on the
    // mirrored element firing its own scroll event to reopen the gate (it may
    // already be at that value or clamped at an edge, in which case no event
    // fires and the gate would stay stuck). Instead, reopen it on the next
    // frame — deterministic regardless of whether a scroll event fired.
    let raf: number | null = null
    const mirror = (from: HTMLElement, to: HTMLElement) => {
      if (syncing.current) return
      syncing.current = true
      to.scrollLeft = from.scrollLeft
      if (raf != null) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        raf = null
        syncing.current = false
      })
    }
    const onTarget = () => mirror(target, proxy)
    const onProxy = () => mirror(proxy, target)

    target.addEventListener('scroll', onTarget, { passive: true })
    proxy.addEventListener('scroll', onProxy, { passive: true })
    proxy.scrollLeft = target.scrollLeft
    return () => {
      target.removeEventListener('scroll', onTarget)
      proxy.removeEventListener('scroll', onProxy)
      if (raf != null) cancelAnimationFrame(raf)
    }
  }, [targetRef, metrics.visible, metrics.scrollWidth])

  if (!metrics.visible || typeof document === 'undefined') return null

  return createPortal(
    <Bar
      ref={proxyRef}
      aria-hidden
      style={{ left: metrics.left, width: metrics.width }}
    >
      <div style={{ width: metrics.scrollWidth, height: 1 }} />
    </Bar>,
    document.body
  )
}

export default StickyHorizontalScrollbar
